import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { seoAPI } from '../../services/api';
import { ScoreBadge, SeverityBadge, shortUrl, SeoResult, SeoScan } from './seoTypes';
import SeoKeywordGate from './SeoKeywordGate';

// ─── Fix Tips ─────────────────────────────────────────────────────────────────

const FIX_TIPS: Record<string, string> = {
  title: 'Write a unique title 50–60 chars. Put the primary keyword near the start.',
  meta_description: 'Write 150–160 chars, include your keyword, and end with a call-to-action.',
  h1: 'Add exactly one H1 that describes the page topic with your primary keyword.',
  heading_hierarchy: 'Add H2/H3 subheadings to break content into logical sections.',
  canonical: 'Add <link rel="canonical" href="…"> in <head> pointing to the preferred URL.',
  open_graph: 'Add og:title, og:description, og:image (1200×630px) meta tags.',
  structured_data: 'Add JSON-LD <script type="application/ld+json"> with Organization or WebPage schema.',
  image_alt: 'Add descriptive alt="" to every <img>. Describe the image and include keywords naturally.',
  robots: 'Remove "noindex" from the robots meta tag or delete the tag entirely.',
  viewport: 'Add <meta name="viewport" content="width=device-width, initial-scale=1"> in <head>.',
  content_length: 'Expand the page to 300+ words. Cover the topic thoroughly.',
  internal_links: 'Link to 3+ other relevant pages using descriptive anchor text.',
  https: 'Install an SSL certificate and redirect all HTTP → HTTPS.',
  twitter_card: 'Add twitter:card, twitter:title, twitter:description, twitter:image meta tags.',
  keyword_in_title: 'Rewrite the title to naturally include your target keyword near the beginning.',
  keyword_in_meta: 'Rewrite the meta description to include your keyword for better CTR.',
  keyword_in_h1: 'Rewrite the H1 heading to naturally include your target keyword.',
};

const FACTORS = [
  { label: 'Title (50-60 chars)', key: 'title', pts: 10 },
  { label: 'Meta Description (150-160 chars)', key: 'meta_description', pts: 8 },
  { label: 'H1 Heading', key: 'h1', pts: 8 },
  { label: 'Heading Hierarchy (H2/H3)', key: 'heading_hierarchy', pts: 5 },
  { label: 'Canonical URL', key: 'canonical', pts: 5 },
  { label: 'Open Graph Tags', key: 'open_graph', pts: 6 },
  { label: 'JSON-LD Structured Data', key: 'structured_data', pts: 10 },
  { label: 'Image Alt Texts', key: 'image_alt', pts: 7 },
  { label: 'Robots Meta', key: 'robots', pts: 8 },
  { label: 'Viewport Meta Tag', key: 'viewport', pts: 5 },
  { label: 'Content Length (300+ words)', key: 'content_length', pts: 8 },
  { label: 'Internal Links (3+)', key: 'internal_links', pts: 5 },
  { label: 'HTTPS', key: 'https', pts: 5 },
  { label: 'Twitter Card', key: 'twitter_card', pts: 5 },
];

// ─── Score Ring ───────────────────────────────────────────────────────────────

const ScoreRing: React.FC<{ score: number; size?: number }> = ({ score, size = 80 }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={8} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={8}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: size < 70 ? 14 : 18, fontWeight: 800, fill: color }}>{score}</text>
    </svg>
  );
};

// ─── Tracked Pages Panel ──────────────────────────────────────────────────────

interface TrackedPage { id: number; url: string; views: number; source: string; is_active: number; }

const TrackedPagesPanel: React.FC<{ assetId: number; onRefreshed?: () => void }> = ({ assetId, onRefreshed }) => {
  const [pages, setPages] = useState<TrackedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { setPages((await seoAPI.getTrackedPages(assetId)).data.data.pages || []); } catch { /* ignore */ }
  };
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [assetId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setPages((await seoAPI.refreshTrackedPages(assetId)).data.data.pages || []);
      toast.success('Pages synced'); onRefreshed?.();
    } catch (err: any) { toast.error(err.response?.data?.message || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };
  const handleRemove = async (p: TrackedPage) => {
    try { await seoAPI.removeTrackedPage(assetId, p.id); setPages(prev => prev.map(x => x.id === p.id ? { ...x, is_active: 0 } : x)); } catch { toast.error('Failed'); }
  };
  const handleRestore = async (p: TrackedPage) => {
    try { await seoAPI.restoreTrackedPage(assetId, p.id); setPages(prev => prev.map(x => x.id === p.id ? { ...x, is_active: 1 } : x)); } catch { toast.error('Failed'); }
  };

  const active = pages.filter(p => p.is_active === 1);
  const excluded = pages.filter(p => p.is_active === 0).length;

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between py-2 px-3"
        style={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span className="fw-semibold small">
          <i className="bi bi-journal-text text-primary me-2"></i>Tracked Pages
          {!loading && <span className="badge bg-primary ms-2" style={{ fontSize: '0.62rem' }}>{active.length}</span>}
          {excluded > 0 && <span className="badge bg-secondary ms-1" style={{ fontSize: '0.62rem' }}>{excluded} excluded</span>}
        </span>
        <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
          <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.72rem' }} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-arrow-clockwise me-1"></i>}
            Sync from Analytics
          </button>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`}></i>
        </div>
      </div>
      {open && (
        <div className="card-body p-0">
          {loading ? <div className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary"></span></div>
            : pages.length === 0 ? <div className="text-center py-3 small text-muted px-3">No pages tracked. <button className="btn btn-link btn-sm p-0" onClick={handleRefresh}>Sync from Analytics</button></div>
              : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="table-light"><tr>
                      <th className="small">URL</th>
                      <th className="small" style={{ width: 70 }}>Views</th>
                      <th className="small" style={{ width: 70 }}>Source</th>
                      <th className="small" style={{ width: 90 }}>Status</th>
                      <th style={{ width: 60 }}></th>
                    </tr></thead>
                    <tbody>
                      {pages.map(p => (
                        <tr key={p.id} style={p.is_active === 0 ? { opacity: 0.45 } : {}}>
                          <td className="small"><a href={p.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-dark">{shortUrl(p.url, 60)}</a></td>
                          <td className="small text-muted">{p.views.toLocaleString()}</td>
                          <td className="small"><span className={`badge ${p.source === 'manual' ? 'bg-info' : 'bg-light text-dark'}`} style={{ fontSize: '0.6rem' }}>{p.source}</span></td>
                          <td className="small">{p.is_active ? <span style={{ color: '#10b981', fontSize: '0.7rem' }}><i className="bi bi-check-circle-fill me-1"></i>Active</span> : <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}><i className="bi bi-slash-circle me-1"></i>Excluded</span>}</td>
                          <td>{p.is_active === 1
                            ? <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.62rem', padding: '1px 5px' }} onClick={() => handleRemove(p)}>Remove</button>
                            : <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.62rem', padding: '1px 5px' }} onClick={() => handleRestore(p)}>Restore</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </div>
      )}
    </div>
  );
};

// ─── Page Report Panel ────────────────────────────────────────────────────────

const printReport = (result: SeoResult, assetName: string) => {
  const issueMap: Record<string, any> = {};
  (result.issues || []).forEach(i => { issueMap[i.factor] = i; });

  const sevColor: Record<string, string> = { critical: '#dc2626', warning: '#d97706', info: '#2563eb' };
  const sevBg: Record<string, string> = { critical: '#fee2e2', warning: '#fef3c7', info: '#dbeafe' };

  const critIssues = FACTORS.filter(f => issueMap[f.key]?.severity === 'critical');
  const warnIssues = FACTORS.filter(f => issueMap[f.key]?.severity === 'warning');
  const passed = FACTORS.filter(f => !issueMap[f.key]);

  const score = result.seo_score || 0;
  const scoreColor = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';

  const issueRows = (factors: typeof FACTORS, sev: string) => factors.map(f => {
    const issue = issueMap[f.key];
    const tip = FIX_TIPS[f.key] || '';
    return `<tr>
      <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;font-weight:600;width:35%">${f.label}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:12px;color:#94a3b8;width:5%">${f.pts}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-size:12px">
        <span style="background:${sevBg[sev]};color:${sevColor[sev]};padding:2px 8px;border-radius:4px;font-size:11px">
          ${issue?.message || sev}
        </span>
        ${tip ? `<div style="margin-top:4px;padding:4px 8px;background:#f0f9ff;border-left:3px solid #38bdf8;border-radius:0 4px 4px 0;font-size:11px;color:#0369a1">${tip}</div>` : ''}
      </td>
    </tr>`;
  }).join('');

  const passedRows = passed.map(f => `<tr>
    <td style="padding:6px 12px;font-size:12px;font-weight:600;width:35%;border-bottom:1px solid #f8fafc">${f.label}</td>
    <td style="padding:6px 12px;font-size:12px;color:#94a3b8;width:5%;border-bottom:1px solid #f8fafc">${f.pts}</td>
    <td style="padding:6px 12px;font-size:12px;color:#10b981;border-bottom:1px solid #f8fafc"><i>✓ Passed</i></td>
  </tr>`).join('');

  const pd = result.page_data;
  const dataGrid = pd ? `
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:16px">
      ${[
        ['Word Count', pd.word_count],
        ['Images (with alt)', `${pd.images_with_alt}/${pd.image_count}`],
        ['Internal Links', pd.internal_links],
        ['H1', pd.h1 ? pd.h1.slice(0, 40) : '—'],
        ['Canonical', pd.has_canonical ? '✓' : '✗'],
        ['Schema', pd.has_schema ? '✓' : '✗'],
        ['OG Tags', pd.has_og ? '✓' : '✗'],
        ['Twitter Card', pd.has_twitter ? '✓' : '✗'],
      ].map(([l, v]) => `<div style="background:#f8fafc;border-radius:6px;padding:8px;text-align:center">
        <div style="font-size:10px;color:#94a3b8;margin-bottom:3px">${l}</div>
        <div style="font-size:13px;font-weight:700">${v}</div>
      </div>`).join('')}
    </div>` : '';

  const html = `<!DOCTYPE html><html><head><title>SEO Report — ${result.url}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 24px; color: #1e293b; }
    @media print { @page { margin: 1cm; } }
    table { width: 100%; border-collapse: collapse; }
  </style></head><body>
  <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #e2e8f0;padding-bottom:16px;margin-bottom:20px">
    <div>
      <div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">${assetName} · SEO Report</div>
      <h1 style="margin:0;font-size:18px;font-weight:700;color:#0f172a">${result.page_title || 'Page Report'}</h1>
      <div style="font-size:12px;color:#6366f1;margin-top:3px">${result.url}</div>
      <div style="font-size:11px;color:#94a3b8;margin-top:2px">Generated ${new Date().toLocaleString()}</div>
    </div>
    <div style="text-align:center;padding:12px 20px;background:${scoreColor}12;border:2px solid ${scoreColor};border-radius:12px">
      <div style="font-size:36px;font-weight:900;color:${scoreColor};line-height:1">${score}</div>
      <div style="font-size:11px;font-weight:700;color:${scoreColor};text-transform:uppercase">${score >= 80 ? 'Good' : score >= 60 ? 'Fair' : 'Poor'}</div>
      <div style="font-size:10px;color:#94a3b8;margin-top:2px">SEO Score</div>
    </div>
  </div>

  <div style="display:flex;gap:16px;margin-bottom:20px">
    <div style="flex:1;background:#fee2e2;border-radius:8px;padding:10px 14px;text-align:center">
      <div style="font-size:22px;font-weight:800;color:#dc2626">${critIssues.length}</div>
      <div style="font-size:11px;color:#991b1b">Critical Issues</div>
    </div>
    <div style="flex:1;background:#fef3c7;border-radius:8px;padding:10px 14px;text-align:center">
      <div style="font-size:22px;font-weight:800;color:#d97706">${warnIssues.length}</div>
      <div style="font-size:11px;color:#92400e">Warnings</div>
    </div>
    <div style="flex:1;background:#dcfce7;border-radius:8px;padding:10px 14px;text-align:center">
      <div style="font-size:22px;font-weight:800;color:#16a34a">${passed.length}</div>
      <div style="font-size:11px;color:#166534">Passed</div>
    </div>
  </div>

  ${critIssues.length > 0 ? `
  <h2 style="font-size:14px;font-weight:700;color:#dc2626;margin:20px 0 8px;padding-bottom:4px;border-bottom:2px solid #fee2e2">⚠ Critical Issues</h2>
  <table>${issueRows(critIssues, 'critical')}</table>` : ''}

  ${warnIssues.length > 0 ? `
  <h2 style="font-size:14px;font-weight:700;color:#d97706;margin:20px 0 8px;padding-bottom:4px;border-bottom:2px solid #fef3c7">⚡ Warnings</h2>
  <table>${issueRows(warnIssues, 'warning')}</table>` : ''}

  ${passed.length > 0 ? `
  <h2 style="font-size:14px;font-weight:700;color:#10b981;margin:20px 0 8px;padding-bottom:4px;border-bottom:2px solid #dcfce7">✓ Passed Checks</h2>
  <table>${passedRows}</table>` : ''}

  ${dataGrid ? `<h2 style="font-size:14px;font-weight:700;color:#475569;margin:20px 0 8px;padding-bottom:4px;border-bottom:2px solid #e2e8f0">Page Data</h2>${dataGrid}` : ''}
  </body></html>`;

  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 400); }
};

const PageReportPanel: React.FC<{ result: SeoResult; assetName: string; onClose: () => void }> = ({ result, assetName, onClose }) => {
  const [activeSection, setActiveSection] = useState<'issues' | 'data'>('issues');
  const issueMap: Record<string, any> = {};
  (result.issues || []).forEach(i => { issueMap[i.factor] = i; });

  const critIssues = FACTORS.filter(f => issueMap[f.key]?.severity === 'critical');
  const warnIssues = FACTORS.filter(f => issueMap[f.key]?.severity === 'warning');
  const passed = FACTORS.filter(f => !issueMap[f.key]);

  const sevColor: Record<string, string> = { critical: '#dc2626', warning: '#d97706', info: '#2563eb' };
  const sevBg: Record<string, string> = { critical: '#fef2f2', warning: '#fffbeb', info: '#eff6ff' };

  const renderIssueGroup = (factors: typeof FACTORS, sev: string, title: string, iconCls: string, headerColor: string) => (
    factors.length > 0 && (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, padding: '4px 0', borderBottom: `2px solid ${headerColor}20` }}>
          <i className={`bi ${iconCls}`} style={{ color: headerColor, fontSize: 13 }}></i>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: headerColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 700, background: headerColor + '18', color: headerColor, padding: '1px 7px', borderRadius: 10 }}>{factors.length}</span>
        </div>
        {factors.map(f => {
          const issue = issueMap[f.key];
          const tip = FIX_TIPS[f.key];
          return (
            <div key={f.key} style={{ background: sevBg[sev], borderRadius: 8, padding: '8px 10px', marginBottom: 6, borderLeft: `3px solid ${sevColor[sev]}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                <i className={`bi ${sev === 'critical' ? 'bi-x-circle-fill' : 'bi-exclamation-triangle-fill'}`} style={{ color: sevColor[sev], fontSize: 12, flexShrink: 0, marginTop: 2 }}></i>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{f.label}</div>
                  <div style={{ fontSize: '0.7rem', color: '#475569', marginBottom: tip ? 5 : 0 }}>{issue?.message}</div>
                  {tip && (
                    <div style={{ fontSize: '0.68rem', color: '#0369a1', background: '#f0f9ff', borderLeft: '3px solid #38bdf8', padding: '3px 7px', borderRadius: '0 4px 4px 0' }}>
                      <i className="bi bi-lightbulb-fill me-1" style={{ color: '#f59e0b' }}></i>{tip}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: '0.6rem', color: '#94a3b8', flexShrink: 0 }}>{f.pts}pt</span>
              </div>
            </div>
          );
        })}
      </div>
    )
  );

  const pd = result.page_data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', borderRadius: 12, border: '1px solid #e9eef4', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <ScoreRing score={result.seo_score || 0} size={70} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
              {result.page_title || 'Untitled Page'}
            </div>
            <a href={result.url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: '0.68rem', color: '#6366f1', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', marginBottom: 6 }}>
              {shortUrl(result.url, 55)}
            </a>
            <div style={{ display: 'flex', gap: 5 }}>
              {critIssues.length > 0 && <span style={{ fontSize: '0.62rem', fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '1px 7px', borderRadius: 10 }}>{critIssues.length} critical</span>}
              {warnIssues.length > 0 && <span style={{ fontSize: '0.62rem', fontWeight: 700, background: '#fef3c7', color: '#d97706', padding: '1px 7px', borderRadius: 10 }}>{warnIssues.length} warnings</span>}
              <span style={{ fontSize: '0.62rem', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 7px', borderRadius: 10 }}>{passed.length} passed</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
            <button onClick={() => printReport(result, assetName)} title="Download PDF Report"
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="bi bi-file-earmark-pdf-fill"></i> PDF
            </button>
            <button onClick={onClose} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 7, padding: '5px 8px', fontSize: '0.72rem', cursor: 'pointer' }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
        {([['issues', 'bi-exclamation-circle', 'Issues'], ['data', 'bi-bar-chart-line', 'Page Data']] as [string, string, string][]).map(([k, icon, label]) => (
          <button key={k} onClick={() => setActiveSection(k as any)}
            style={{ flex: 1, padding: '8px 0', border: 'none', background: 'none', fontSize: '0.75rem', fontWeight: 600,
              color: activeSection === k ? '#6366f1' : '#94a3b8',
              borderBottom: activeSection === k ? '2px solid #6366f1' : '2px solid transparent',
              cursor: 'pointer', transition: 'color 0.15s' }}>
            <i className={`bi ${icon} me-1`}></i>{label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 20px' }}>
        {activeSection === 'issues' && (
          <>
            {renderIssueGroup(critIssues, 'critical', 'Critical Issues', 'bi-x-circle-fill', '#dc2626')}
            {renderIssueGroup(warnIssues, 'warning', 'Warnings', 'bi-exclamation-triangle-fill', '#d97706')}
            {passed.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, padding: '4px 0', borderBottom: '2px solid #dcfce720' }}>
                  <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: 13 }}></i>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Passed Checks</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 7px', borderRadius: 10 }}>{passed.length}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {passed.map(f => (
                    <span key={f.key} style={{ fontSize: '0.68rem', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 8px' }}>
                      <i className="bi bi-check me-1" style={{ color: '#10b981' }}></i>{f.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {critIssues.length === 0 && warnIssues.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#10b981' }}>
                <i className="bi bi-patch-check-fill" style={{ fontSize: 32, display: 'block', marginBottom: 8 }}></i>
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>No issues found!</div>
              </div>
            )}
          </>
        )}

        {activeSection === 'data' && pd && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { l: 'Word Count', v: pd.word_count, icon: 'bi-file-text', ok: pd.word_count >= 300 },
              { l: 'Images / Alt', v: `${pd.images_with_alt}/${pd.image_count}`, icon: 'bi-image', ok: pd.images_with_alt === pd.image_count },
              { l: 'Internal Links', v: pd.internal_links, icon: 'bi-link-45deg', ok: pd.internal_links >= 3 },
              { l: 'H1 Heading', v: pd.h1 ? pd.h1.slice(0, 30) + (pd.h1.length > 30 ? '…' : '') : '—', icon: 'bi-type-h1', ok: !!pd.h1 },
              { l: 'Canonical', v: pd.has_canonical ? 'Present' : 'Missing', icon: 'bi-link', ok: pd.has_canonical },
              { l: 'Schema (JSON-LD)', v: pd.has_schema ? 'Present' : 'Missing', icon: 'bi-code-slash', ok: pd.has_schema },
              { l: 'Open Graph', v: pd.has_og ? 'Present' : 'Missing', icon: 'bi-share', ok: pd.has_og },
              { l: 'Twitter Card', v: pd.has_twitter ? 'Present' : 'Missing', icon: 'bi-twitter-x', ok: pd.has_twitter },
            ].map(d => (
              <div key={d.l} style={{ background: d.ok ? '#f0fdf4' : '#fff7ed', border: `1px solid ${d.ok ? '#bbf7d0' : '#fde68a'}`, borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                  <i className={`bi ${d.icon}`} style={{ fontSize: 11, color: d.ok ? '#10b981' : '#f59e0b' }}></i>
                  <span style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{d.l}</span>
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{String(d.v)}</div>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'data' && !pd && (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '0.8rem' }}>No page data available</div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const SeoScannerPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const assetName = currentAsset?.name || 'Asset';

  const [scans, setScans] = useState<SeoScan[]>([]);
  const [scansLoading, setScansLoading] = useState(true);
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);
  const [selectedResults, setSelectedResults] = useState<SeoResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [detailResult, setDetailResult] = useState<SeoResult | null>(null);
  const [filterSev, setFilterSev] = useState<'all' | 'critical' | 'warning' | 'ok'>('all');
  const [searchQ, setSearchQ] = useState('');

  const [activeScanId, setActiveScanId] = useState<number | null>(null);
  const [activeScanStatus, setActiveScanStatus] = useState<any>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadScans = useCallback(async () => {
    if (!assetId) return;
    try {
      const res = await seoAPI.getScans(assetId);
      const list: SeoScan[] = res.data.data.scans || [];
      setScans(list);
      if (list.length > 0 && !selectedScanId) setSelectedScanId(list[0].id);
      const running = list.find(s => s.status === 'running' || s.status === 'pending');
      if (running && !activeScanId) setActiveScanId(running.id);
    } catch { /* ignore */ }
  }, [assetId, selectedScanId, activeScanId]);

  useEffect(() => {
    if (!assetId) return;
    setScansLoading(true);
    loadScans().finally(() => setScansLoading(false));
  }, [assetId]);

  useEffect(() => {
    if (!selectedScanId) return;
    setResultsLoading(true); setDetailResult(null);
    seoAPI.getScanStatus(selectedScanId)
      .then(res => setSelectedResults(res.data.data.results || []))
      .catch(() => {})
      .finally(() => setResultsLoading(false));
  }, [selectedScanId]);

  const pollScan = useCallback(async (id: number) => {
    try {
      const res = await seoAPI.getScanStatus(id);
      const data = res.data.data;
      setActiveScanStatus(data);
      if (data.status === 'completed' || data.status === 'failed') {
        if (pollRef.current) clearInterval(pollRef.current);
        setActiveScanId(null);
        if (data.status === 'completed') {
          toast.success('Scan complete!');
          await loadScans();
          setSelectedScanId(id);
          setSelectedResults(data.results || []);
        }
      }
    } catch { /* ignore */ }
  }, [loadScans]);

  useEffect(() => {
    if (activeScanId) pollRef.current = setInterval(() => pollScan(activeScanId), 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeScanId, pollScan]);

  const handleStartScan = async () => {
    if (!assetId) return;
    setStarting(true);
    try {
      const res = await seoAPI.startScan(assetId);
      const { scan_id } = res.data.data;
      setActiveScanId(scan_id); setActiveScanStatus(null);
      toast.success('Scan started!');
      await loadScans(); setSelectedScanId(scan_id);
    } catch (err: any) { toast.error(err.response?.data?.message || 'Failed to start scan'); }
    finally { setStarting(false); }
  };

  const isRunning = !!activeScanId;
  const activeProg = activeScanStatus?.progress || 0;
  const activeScanned = activeScanStatus?.scanned || 0;
  const activeTotal = activeScanStatus?.total || 0;

  const selectedScan = scans.find(s => s.id === selectedScanId);
  const completed = selectedResults.filter(r => r.status === 'completed');
  const avgScore = completed.length ? Math.round(completed.reduce((s, r) => s + (r.seo_score || 0), 0) / completed.length) : 0;
  const critCount = completed.flatMap(r => r.issues || []).filter(i => i.severity === 'critical').length;
  const warnCount = completed.flatMap(r => r.issues || []).filter(i => i.severity === 'warning').length;

  const filteredResults = selectedResults.filter(r => {
    if (searchQ && !r.url.toLowerCase().includes(searchQ.toLowerCase()) && !(r.page_title || '').toLowerCase().includes(searchQ.toLowerCase())) return false;
    if (filterSev === 'critical') return (r.issues || []).some(i => i.severity === 'critical');
    if (filterSev === 'warning') return (r.issues || []).some(i => i.severity === 'warning');
    if (filterSev === 'ok') return r.status === 'completed' && (r.issues || []).filter(i => i.severity === 'critical' || i.severity === 'warning').length === 0;
    return true;
  });

  if (!assetId) return <DashboardLayout><div className="p-4 text-muted">Select an asset first.</div></DashboardLayout>;

  const hasDetailOpen = !!detailResult;

  return (
    <DashboardLayout>
      <SeoKeywordGate assetId={assetId}>
      <div style={{ padding: '16px 24px' }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-search text-primary me-2"></i>SEO Scanner</h4>
            <p className="text-muted small mb-0">Scan public pages · view detailed reports · download PDF</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleStartScan} disabled={isRunning || starting}>
            {isRunning ? <><span className="spinner-border spinner-border-sm me-1"></span>Scanning…</>
              : starting ? <><span className="spinner-border spinner-border-sm me-1"></span>Starting…</>
                : <><i className="bi bi-play-fill me-1"></i>New Scan</>}
          </button>
        </div>

        <TrackedPagesPanel assetId={assetId} onRefreshed={loadScans} />

        {isRunning && (
          <div className="card border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #3b82f6' }}>
            <div className="card-body py-2 px-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small fw-semibold"><i className="bi bi-arrow-repeat text-primary me-1"></i>Scanning page {activeScanned} of {activeTotal}…</span>
                <span className="fw-bold text-primary small">{activeProg}%</span>
              </div>
              <div className="progress" style={{ height: 6 }}>
                <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" style={{ width: `${activeProg}%` }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Main grid */}
        <div style={{ display: 'grid', gridTemplateColumns: hasDetailOpen ? '180px 1fr 380px' : '200px 1fr', gap: 14, alignItems: 'start' }}>

          {/* Scan History */}
          <div className="card border-0 shadow-sm" style={{ overflow: 'hidden' }}>
            <div className="card-header bg-white border-0 pt-3 pb-2 px-3">
              <h6 className="fw-bold mb-0" style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px' }}>Scan History</h6>
            </div>
            <div className="list-group list-group-flush">
              {scansLoading
                ? <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary"></span></div>
                : scans.length === 0
                  ? <div className="text-center py-4 small text-muted px-3">No scans yet</div>
                  : scans.map(s => {
                    const isActive = s.id === selectedScanId;
                    const sc = s.status === 'completed' ? '#10b981' : s.status === 'failed' ? '#ef4444' : '#3b82f6';
                    const ic = s.status === 'completed' ? 'bi-check-circle-fill' : s.status === 'failed' ? 'bi-x-circle-fill' : 'bi-arrow-repeat';
                    return (
                      <button key={s.id} onClick={() => { setSelectedScanId(s.id); setDetailResult(null); }}
                        className={`list-group-item list-group-item-action border-0 text-start ${isActive ? 'active' : ''}`}
                        style={{ borderRadius: 0, padding: '8px 12px' }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{new Date(s.created_at).toLocaleDateString()}</span>
                          <i className={`bi ${ic}`} style={{ color: isActive ? '#fff' : sc, fontSize: '0.75rem' }}></i>
                        </div>
                        <div style={{ fontSize: '0.68rem', opacity: 0.8 }}>{s.total_pages} pages · {s.status}</div>
                      </button>
                    );
                  })}
            </div>
          </div>

          {/* Results */}
          <div>
            {!selectedScanId ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5 text-muted">
                  <i className="bi bi-search" style={{ fontSize: '2.5rem', opacity: 0.25 }}></i>
                  <p className="mt-2 mb-3 small">Start a scan or select one from history</p>
                  <button className="btn btn-primary btn-sm" onClick={handleStartScan} disabled={isRunning || starting}><i className="bi bi-play-fill me-1"></i>Start Scan</button>
                </div>
              </div>
            ) : (
              <>
                {/* Summary bar */}
                {completed.length > 0 && (
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body py-2 px-3">
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ScoreRing score={avgScore} size={46} />
                          <div>
                            <div style={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Avg Score</div>
                            <div style={{ fontSize: '0.7rem', color: '#475569' }}>{completed.length} pages scanned</div>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <SeverityBadge severity="critical" count={critCount} label="critical" />
                          <SeverityBadge severity="warning" count={warnCount} label="warnings" />
                        </div>
                        <div className="ms-auto d-flex gap-2">
                          {selectedScan?.completed_at && (
                            <span className="small text-muted">{new Date(selectedScan.completed_at).toLocaleString()}</span>
                          )}
                          <button onClick={() => {
                            // Print all pages report
                            const win = window.open('', '_blank');
                            if (win) {
                              const rows = completed.map(r => {
                                const crit = (r.issues || []).filter(i => i.severity === 'critical').length;
                                const warn = (r.issues || []).filter(i => i.severity === 'warning').length;
                                const sc = r.seo_score || 0;
                                const c = sc >= 80 ? '#10b981' : sc >= 60 ? '#f59e0b' : '#ef4444';
                                return `<tr><td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px"><a href="${r.url}" style="color:#6366f1">${r.url}</a><br><span style="color:#94a3b8;font-size:10px">${r.page_title || ''}</span></td>
                                  <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;text-align:center"><span style="font-size:18px;font-weight:800;color:${c}">${sc}</span></td>
                                  <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;text-align:center"><span style="color:#dc2626;font-weight:700">${crit}</span></td>
                                  <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;text-align:center"><span style="color:#d97706;font-weight:700">${warn}</span></td></tr>`;
                              }).join('');
                              win.document.write(`<!DOCTYPE html><html><head><title>SEO Report — ${assetName}</title><style>body{font-family:-apple-system,sans-serif;padding:24px}table{width:100%;border-collapse:collapse}</style></head><body>
                                <h1 style="font-size:18px;margin-bottom:4px">SEO Scan Report — ${assetName}</h1>
                                <p style="color:#94a3b8;font-size:12px;margin-top:0">${new Date().toLocaleString()} · ${completed.length} pages · Avg score: ${avgScore}</p>
                                <table><thead><tr style="background:#f8fafc"><th style="padding:8px 10px;text-align:left;font-size:12px">Page URL</th><th style="padding:8px 10px;width:80px;font-size:12px">Score</th><th style="padding:8px 10px;width:80px;font-size:12px">Critical</th><th style="padding:8px 10px;width:80px;font-size:12px">Warnings</th></tr></thead><tbody>${rows}</tbody></table>
                                </body></html>`);
                              win.document.close(); setTimeout(() => win.print(), 400);
                            }
                          }} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-file-earmark-pdf-fill"></i> Full Report
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filter bar */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: 1, maxWidth: 260 }}>
                    <i className="bi bi-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }}></i>
                    <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Filter pages…"
                      style={{ width: '100%', padding: '5px 9px 5px 28px', fontSize: '0.78rem', border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', background: '#fff' }} />
                  </div>
                  {(['all', 'critical', 'warning', 'ok'] as const).map(f => (
                    <button key={f} onClick={() => setFilterSev(f)}
                      style={{ padding: '4px 12px', fontSize: '0.72rem', fontWeight: 600, border: '1px solid',
                        borderColor: filterSev === f ? '#6366f1' : '#e2e8f0',
                        background: filterSev === f ? '#6366f1' : '#fff',
                        color: filterSev === f ? '#fff' : '#64748b',
                        borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s', textTransform: 'capitalize' }}>
                      {f === 'ok' ? '✓ Clean' : f === 'all' ? 'All' : f}
                    </button>
                  ))}
                </div>

                {/* Results list */}
                {resultsLoading ? (
                  <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>
                ) : filteredResults.length === 0 ? (
                  <div className="card border-0 shadow-sm"><div className="text-center py-4 text-muted small">No results match the filter</div></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {filteredResults.map(r => {
                      const crit = (r.issues || []).filter(i => i.severity === 'critical').length;
                      const warn = (r.issues || []).filter(i => i.severity === 'warning').length;
                      const isSelected = detailResult?.id === r.id;
                      const sc = r.seo_score || 0;
                      const scoreColor = sc >= 80 ? '#10b981' : sc >= 60 ? '#f59e0b' : '#ef4444';
                      return (
                        <div key={r.id} onClick={() => r.status === 'completed' && setDetailResult(isSelected ? null : r)}
                          style={{
                            background: isSelected ? '#f0f4ff' : '#fff',
                            border: `1px solid ${isSelected ? '#6366f1' : crit > 0 ? '#fecaca' : warn > 0 ? '#fde68a' : '#e9eef4'}`,
                            borderRadius: 10, padding: '10px 14px',
                            cursor: r.status === 'completed' ? 'pointer' : 'default',
                            transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 12,
                            boxShadow: isSelected ? '0 0 0 2px #6366f140' : '0 1px 3px rgba(0,0,0,0.04)',
                          }}
                          onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                          onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = '#fff'; }}
                        >
                          {/* Score */}
                          {r.status === 'completed' ? (
                            <div style={{ textAlign: 'center', minWidth: 42, flexShrink: 0 }}>
                              <div style={{ fontSize: '1.3rem', fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{sc}</div>
                              <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden', marginTop: 3 }}>
                                <div style={{ height: '100%', width: `${sc}%`, background: scoreColor, borderRadius: 2, transition: 'width 0.4s' }} />
                              </div>
                            </div>
                          ) : (
                            <div style={{ width: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {r.status === 'scanning' ? <span className="spinner-border spinner-border-sm text-primary"></span>
                                : r.status === 'failed' ? <i className="bi bi-x-circle-fill text-danger"></i>
                                  : <span style={{ fontSize: '0.62rem', color: '#94a3b8' }}>Pending</span>}
                            </div>
                          )}

                          {/* URL info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.page_title || shortUrl(r.url, 50)}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortUrl(r.url, 55)}</div>
                          </div>

                          {/* Badges */}
                          {r.status === 'completed' && (
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                              {crit > 0 && <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '1px 6px', borderRadius: 8 }}>{crit} crit</span>}
                              {warn > 0 && <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#fef3c7', color: '#d97706', padding: '1px 6px', borderRadius: 8 }}>{warn} warn</span>}
                              {crit === 0 && warn === 0 && <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 6px', borderRadius: 8 }}>✓ Clean</span>}
                              <i className={`bi ${isSelected ? 'bi-chevron-right text-primary' : 'bi-chevron-right text-muted'}`} style={{ fontSize: 11, marginLeft: 2 }}></i>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Detail panel */}
          {hasDetailOpen && detailResult && (
            <div style={{ position: 'sticky', top: 16, maxHeight: 'calc(100vh - 100px)' }}>
              <PageReportPanel result={detailResult} assetName={assetName} onClose={() => setDetailResult(null)} />
            </div>
          )}

        </div>
      </div>
      </SeoKeywordGate>
    </DashboardLayout>
  );
};

export default SeoScannerPage;
